import {Suspense, useMemo} from 'react'
import type {OptionConfig} from './OptionConfig'
import type {IssueViewerViewer$data} from './__generated__/IssueViewerViewer.graphql'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'

import {noop} from '@github-ui/noop'
import {EditIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {EditIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {OldExperienceReloadBanner} from './OldExperienceReloadBanner'
import {OptionsSection} from './sections/OptionsSection'
import {ParticipantsSection} from './sections/ParticipantsSection'
import {graphql, useFragment} from 'react-relay'
import type {IssueSidebarPrimaryQuery$key} from './__generated__/IssueSidebarPrimaryQuery.graphql'
import {IssueSidebarLazySections, IssueSidebarLazySectionsFallback} from './IssueSidebarLazySections'
import {EditIssueProjectsSection} from '@github-ui/issue-metadata/ProjectsSection'
import {EditIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'

type IssueSidebarProps = {
  sidebarKey: IssueSidebarPrimaryQuery$key
  optionConfig: OptionConfig
  viewer: IssueViewerViewer$data
}

export const IssueSidebarPrimaryGraphqlQuery = graphql`
  fragment IssueSidebarPrimaryQuery on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    number
    repository {
      name
      owner {
        login
      }
    }
    ...AssigneesSectionFragment
    ...LabelsSectionFragment
    ...ProjectsSectionFragment @arguments(allowedOwner: $allowedOwner)
    ...MilestonesSectionFragment
    ...ParticipantsSectionFragment
    ...OptionsSectionFragment
  }
`

export function IssueSidebar({sidebarKey, optionConfig, viewer}: IssueSidebarProps) {
  const issue = useFragment(IssueSidebarPrimaryGraphqlQuery, sidebarKey)
  const {tasklist_block} = useFeatureFlags()

  const sidebar = useMemo(
    () => (
      <>
        <EditIssueAssigneesSection
          issue={issue}
          viewer={viewer}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        <EditIssueLabelsSection
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        <PreloadedQueryBoundary
          onRetry={noop}
          fallback={() => {
            return (
              <Section
                sectionHeader={<ReadonlySectionHeader title={'Projects are currently unavailable'} />}
                emptyText={'Please try again later'}
              >
                <></>
              </Section>
            )
          }}
        >
          <EditIssueProjectsSection
            selectedProjectId={optionConfig.selectedProjectId}
            allowedProjectOwner={optionConfig.allowedProjectOwner}
            issueOrPullRequest={issue}
            onIssueUpdate={optionConfig.onIssueUpdate}
            singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
            prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
          />
        </PreloadedQueryBoundary>
        <EditIssueMilestonesSection
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
          prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
        />
        <Suspense fallback={<IssueSidebarLazySectionsFallback tasklist_block={tasklist_block} />}>
          <IssueSidebarLazySections
            onLinkClick={optionConfig.onLinkClick}
            onIssueUpdate={optionConfig.onIssueUpdate}
            prefetchInteractionDataEnabled={optionConfig.prefetchInteractionDataEnabled || false}
            singleKeyShortcutsEnabled={optionConfig.singleKeyShortcutsEnabled || false}
            tasklist_block={tasklist_block}
            owner={issue.repository.owner.login}
            repo={issue.repository.name}
            issueNumber={issue.number}
          />
        </Suspense>
        <ParticipantsSection issue={issue} />
        <OptionsSection issue={issue} optionConfig={optionConfig} />
        {optionConfig.showReloadInOldExperience && <OldExperienceReloadBanner />}
      </>
    ),
    [issue, optionConfig, tasklist_block, viewer],
  )

  return sidebar
}

try{ IssueSidebar.displayName ||= 'IssueSidebar' } catch {}